import React, { useEffect, useState } from 'react';
import api from '../api';
import { StatCard, PageHeader, useToast, Modal, Badge, Avatar } from '../ui';
import { useLang } from '../Layout';
import { FaUsers, FaUserGear, FaPlus } from 'react-icons/fa6';

const T = {
  bn: {
    title: 'প্রজেক্ট', profile: 'প্রজেক্ট প্রোফাইল', name: 'নাম', address: 'ঠিকানা', desc: 'বিবরণ', status: 'স্ট্যাটাস',
    active: 'সক্রিয়', paused: 'বিরতি', completed: 'সমাপ্ত', save: 'সংরক্ষণ করুন', saved: 'সংরক্ষিত হয়েছে',
    workers: 'মোট কর্মী', contractors: 'কন্ট্রাক্টর', staff: 'ম্যানেজার অ্যাকাউন্ট', newManager: 'নতুন ম্যানেজার',
    phone: 'ফোন', password: 'পাসওয়ার্ড', emptyManagers: 'কোনো ম্যানেজার অ্যাকাউন্ট নেই।',
  },
  en: {
    title: 'Project', profile: 'Project Profile', name: 'Name', address: 'Address', desc: 'Description', status: 'Status',
    active: 'Active', paused: 'Paused', completed: 'Completed', save: 'Save', saved: 'Saved',
    workers: 'Total Workers', contractors: 'Contractors', staff: 'Manager Accounts', newManager: 'New Manager',
    phone: 'Phone', password: 'Password', emptyManagers: 'No manager accounts yet.',
  },
};

export default function Project() {
  const { lang } = useLang(); const t = T[lang];
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [staff, setStaff] = useState([]);
  const [showManager, setShowManager] = useState(false);
  const [mgr, setMgr] = useState({ name: '', phone: '', password: '' });
  const [err, setErr] = useState('');

  const loadStaff = () => api.get('/owner/staff').then(({ data }) => setStaff(data.staff || [])).catch(() => {});
  useEffect(() => { api.get('/owner').then(({ data }) => { setData(data); setForm({ ...data.project }); }).catch(() => {}); loadStaff(); }, []);

  async function save(e) {
    e.preventDefault();
    const { data: p } = await api.put('/owner/project', form);
    setData((d) => ({ ...d, project: p.project })); toast(t.saved, 'success');
  }

  async function addManager(e) {
    e.preventDefault(); setErr('');
    try {
      const resp = await api.post('/owner/staff', mgr);
      toast(resp.data.message, 'success');
      setShowManager(false); setMgr({ name: '', phone: '', password: '' }); loadStaff();
    } catch (e) { setErr(e.response?.data?.message || 'সমস্যা হয়েছে'); }
  }

  async function toggleManager(m) {
    await api.put(`/owner/staff/${m.id}`, { is_active: !m.is_active });
    loadStaff();
  }

  if (!data || !form) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5">
      <PageHeader title={t.title} subtitle={t.profile} />

      <div className="grid grid-cols-2 gap-4">
        <StatCard icon={<FaUsers />} tone="sky" label={t.workers} value={data.stats.workers} />
        <StatCard icon={<FaUserGear />} tone="amber" label={t.contractors} value={data.stats.contractors} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Project profile */}
        <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-5 space-y-4">
          <h3 className="font-semibold text-ink-900">{t.profile}</h3>
          <div><label className="label">{t.name}</label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div><label className="label">{t.address}</label><input className="input" value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
          <div><label className="label">{t.desc}</label><textarea className="input" rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div>
            <label className="label">{t.status}</label>
            <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">{t.active}</option><option value="paused">{t.paused}</option><option value="completed">{t.completed}</option>
            </select>
          </div>
          <button onClick={save} className="btn-primary w-full py-3">{t.save}</button>
        </div>

        {/* Manager accounts */}
        <div className="bg-white rounded-xl border border-ink-100 shadow-soft p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-ink-900">{t.staff}</h3>
            <button className="btn-soft text-sm" onClick={() => { setShowManager(true); setErr(''); }}>+ {t.newManager}</button>
          </div>
          <div className="space-y-2.5">
            {staff.map((m) => (
              <div key={m.id} className="flex items-center gap-3 bg-ink-50 rounded-xl px-3.5 py-3">
                <Avatar name={m.name} className="w-9 h-9 text-xs" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-ink-800 truncate">{m.name}</div>
                  <div className="text-xs text-ink-400">{m.phone}</div>
                </div>
                <button onClick={() => toggleManager(m)}>
                  <Badge color={m.is_active ? 'active' : 'paused'}>{m.is_active ? t.active : t.inactive}</Badge>
                </button>
              </div>
            ))}
            {staff.length === 0 && <div className="text-sm text-ink-400 py-8 text-center">{t.emptyManagers}</div>}
          </div>
        </div>
      </div>

      <Modal open={showManager} onClose={() => setShowManager(false)} title={t.newManager}>
        <form onSubmit={addManager} className="space-y-3.5">
          {err && <div className="text-sm bg-rose-50 text-rose-600 px-3 py-2 rounded-xl">⚠️ {err}</div>}
          <div><label className="label">{t.name} <span className="text-rose-500">*</span></label><input className="input" placeholder="যেমন: রফিক মিয়া" value={mgr.name} onChange={(e) => setMgr({ ...mgr, name: e.target.value })} required /></div>
          <div><label className="label">{t.phone} <span className="text-rose-500">*</span></label><input className="input" placeholder="01XXXXXXXXX" value={mgr.phone} onChange={(e) => setMgr({ ...mgr, phone: e.target.value })} inputMode="tel" required /></div>
          <div><label className="label">{t.password} <span className="text-rose-500">*</span></label><input className="input" type="password" value={mgr.password} onChange={(e) => setMgr({ ...mgr, password: e.target.value })} required placeholder="কমপক্ষে ৬ অক্ষর" /></div>
          <button className="btn-primary w-full py-3">{t.newManager}</button>
        </form>
      </Modal>
    </div>
  );
}